import React, { useCallback, useEffect, useState } from 'react';
import type { AdventureSnapshotListItem } from '../../types';
import { formatPlaythroughSnapshotLabel } from '../../constants/playthroughSnapshot';
import useGameStore from '../../stores/gameStore';
import useUIStore from '../../stores/uiStore';
import { useGameState } from '../../hooks/useGameState';
import OptionButton from './OptionButton';
import { LoadingSpinner } from '../ui';

export interface PlaythroughSnapshotMenuOptionsProps {
  disabled?: boolean;
  waiting?: boolean;
  customColor?: string;
}

function selectActionErrorMessage(err: unknown, fallback: string): string {
  if (
    err &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as { message: unknown }).message === 'string'
  ) {
    const message = ((err as { message: string }).message || '').trim();
    if (message) {
      return message;
    }
  }
  return fallback;
}

const PlaythroughSnapshotMenuOptions: React.FC<PlaythroughSnapshotMenuOptionsProps> = ({
  disabled = false,
  waiting = false,
  customColor,
}) => {
  const listPlaythroughSnapshots = useGameStore((state) => state.listPlaythroughSnapshots);
  const loadPlaythroughSnapshot = useGameStore((state) => state.loadPlaythroughSnapshot);
  const gameState = useGameState();
  const uiStore = useUIStore();

  const [snapshots, setSnapshots] = useState<AdventureSnapshotListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingSnapshotPath, setLoadingSnapshotPath] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadSnapshots = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const result = await listPlaythroughSnapshots();
        if (!cancelled) {
          setSnapshots(result.snapshots);
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(selectActionErrorMessage(error, 'Failed to load playthrough snapshots'));
          setSnapshots([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadSnapshots();
    return () => {
      cancelled = true;
    };
  }, [listPlaythroughSnapshots]);

  const handleSnapshotSelect = useCallback(
    async (snapshotPath: string) => {
      if (disabled || waiting || loadingSnapshotPath) {
        return;
      }

      setLoadingSnapshotPath(snapshotPath);
      gameState.beginPendingAction('Restoring snapshot...');
      try {
        await loadPlaythroughSnapshot(snapshotPath);
        uiStore.showToast('Snapshot restored', 'success');
      } catch (error) {
        uiStore.showToast(
          selectActionErrorMessage(error, 'Failed to restore playthrough snapshot'),
          'error'
        );
      } finally {
        setLoadingSnapshotPath(null);
        uiStore.clearActionPending();
      }
    },
    [disabled, waiting, loadingSnapshotPath, gameState, loadPlaythroughSnapshot, uiStore]
  );

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-2 py-2" data-testid="playthrough-snapshot-menu-loading">
        <LoadingSpinner />
        <p className="text-sm text-indigo-100">Loading snapshots...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <p className="text-sm text-red-200 py-2" data-testid="playthrough-snapshot-menu-error">
        {loadError}
      </p>
    );
  }

  if (snapshots.length === 0) {
    return (
      <p className="text-sm text-indigo-100 py-2" data-testid="playthrough-snapshot-menu-empty">
        No playthrough snapshots found for this adventure.
      </p>
    );
  }

  return (
    <div
      style={snapshotMenuStyles.list}
      data-testid="playthrough-snapshot-menu-options"
    >
      {snapshots.map((snapshot) => (
        <div key={snapshot.path} style={snapshotMenuStyles.optionWrapper}>
          <OptionButton
            text={formatPlaythroughSnapshotLabel(snapshot)}
            onClick={() => {
              void handleSnapshotSelect(snapshot.path);
            }}
            disabled={disabled || loadingSnapshotPath !== null}
            waiting={waiting || loadingSnapshotPath === snapshot.path}
            variant="glass-primary"
            fullWidth
            {...(customColor !== undefined ? { customColor } : {})}
          />
        </div>
      ))}
    </div>
  );
};

const snapshotMenuStyles = {
  list: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1.25rem',
    marginTop: '0.5rem',
    width: '100%',
  },
  optionWrapper: {
    width: '100%',
  },
};

export default PlaythroughSnapshotMenuOptions;
